import { type ImportDefinitionContract, ImportOnErrorStrategy } from '@today/types'
import { MISSING_RECORD_POLICY, STRATEGY_LABELS } from './processing-policy'

type Processing = ImportDefinitionContract['processing']

const INPUT_CLASS =
	'h-9 rounded-ds-md border border-ds-border bg-ds-surface px-2 text-ds-sm text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring'

/**
 * Props for {@link ProcessingStep}.
 *
 * @property processing - The contract's processing block.
 * @property keyCandidates - The mapped canonical fields a key may use.
 * @property missing - What a keyed strategy still lacks (`key`, `sourceSystem`); shown as a hint.
 * @property onChange - Called with the whole new processing block.
 */
export interface ProcessingStepProps {
	processing: Processing
	keyCandidates: readonly string[]
	missing: readonly ('key' | 'sourceSystem')[]
	onChange: (processing: Processing) => void
}

const ON_ERROR_LABELS: Record<ImportOnErrorStrategy, string> = {
	[ImportOnErrorStrategy.REJECT_FILE]: 'Reject the whole file',
	[ImportOnErrorStrategy.SKIP_INVALID_RECORDS]: 'Skip invalid records',
}

function KeyFields({ processing, keyCandidates, onChange }: Omit<ProcessingStepProps, 'missing'>) {
	const toggle = (field: string, on: boolean) =>
		onChange({ ...processing, key: on ? [...processing.key, field] : processing.key.filter((k) => k !== field) })
	return (
		<fieldset className="flex flex-col gap-1">
			<legend>Key fields</legend>
			{keyCandidates.map((field) => (
				<label key={field} className="flex items-center gap-2">
					<input
						type="checkbox"
						checked={processing.key.includes(field)}
						onChange={(e) => toggle(field, e.target.checked)}
					/>
					<span>{field}</span>
				</label>
			))}
		</fieldset>
	)
}

function EnumSelect<T extends string>({
	label,
	value,
	labels,
	onChange,
}: {
	label: string
	value: T
	labels: Readonly<Record<T, string>>
	onChange: (value: T) => void
}) {
	return (
		<label className="flex flex-col gap-1">
			<span>{label}</span>
			<select className={INPUT_CLASS} value={value} onChange={(e) => onChange(e.target.value as T)}>
				{(Object.keys(labels) as T[]).map((v) => (
					<option key={v} value={v}>
						{labels[v]}
					</option>
				))}
			</select>
		</label>
	)
}

const missingText = (missing: ProcessingStepProps['missing']) =>
	missing.map((m) => (m === 'key' ? 'a key field' : 'a source system (a-z, 0-9, _ or -)')).join(' and ')

/**
 * The processing strategy, key, source system and on-error choice (TO-400
 * SCN-009). The missing-record policy is read-only text derived from the
 * strategy (BR-10). Pure.
 */
export function ProcessingStep({ processing, keyCandidates, missing, onChange }: ProcessingStepProps) {
	const set = (patch: Partial<Processing>) => onChange({ ...processing, ...patch })
	return (
		<div className="flex flex-col gap-3 text-ds-sm text-ds-text">
			<EnumSelect
				label="Strategy"
				value={processing.strategy}
				labels={STRATEGY_LABELS}
				onChange={(strategy) => set({ strategy })}
			/>
			<p role="note" aria-label="Missing-record policy">
				{MISSING_RECORD_POLICY[processing.strategy]}
			</p>
			<KeyFields processing={processing} keyCandidates={keyCandidates} onChange={onChange} />
			<label className="flex flex-col gap-1">
				<span>Source system</span>
				<input
					className={INPUT_CLASS}
					value={processing.sourceSystem ?? ''}
					onChange={(e) => set({ sourceSystem: e.target.value === '' ? null : e.target.value })}
				/>
			</label>
			<EnumSelect
				label="On invalid records"
				value={processing.onError.strategy}
				labels={ON_ERROR_LABELS}
				onChange={(strategy) => set({ onError: { strategy } })}
			/>
			<label className="flex items-center gap-2">
				<input type="checkbox" checked={processing.atomic} onChange={(e) => set({ atomic: e.target.checked })} />
				<span>Apply all changes in one step (atomic)</span>
			</label>
			{missing.length > 0 && (
				<p role="status" className="text-ds-danger">
					{STRATEGY_LABELS[processing.strategy]} needs {missingText(missing)}.
				</p>
			)}
		</div>
	)
}
